import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Row, Col, Card, Button, Form, Modal, Badge, Spinner, Alert, Table
} from 'react-bootstrap';
import {
  FolderPlus, PencilSquare, Trash3, Tags, Layers, Check2Circle, PlusCircle, ArrowUpShort, ArrowDownShort
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import Container from '../../layouts/Container';
import {
  getFormGroups,
  getFieldGroups,
  createFieldGroup,
  updateFieldGroup,
  deleteFieldGroup,
  reorderFieldGroups
} from '../../services/project/fieldGroupsService';
import { useUsers } from '../../context/UserContext';

export default function FieldGroupsPage() {
  const { projectId } = useParams();
  const { user } = useUsers();

  const [formGroups, setFormGroups] = useState([]);
  const [selectedFormGroupId, setSelectedFormGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [groupsData, setGroupsData] = useState({ groups: [], ungroupedFields: [] });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states
  const [groupLabelInput, setGroupLabelInput] = useState('');
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const [activeGroupLabel, setActiveGroupLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filter & Search states
  const [fieldSearch, setFieldSearch] = useState('');

  // Fetch FormGroups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const data = await getFormGroups(projectId);
        const list = Array.isArray(data) ? data : [];
        setFormGroups(list);
        if (list.length > 0) {
          const activeGroup = list.find(g => g.isActive) || list[0];
          setSelectedFormGroupId(String(activeGroup.id));
        }
      } catch (err) {
        toast.error('Failed to load form groups');
      }
    };
    fetchGroups();
  }, [projectId]);

  // Fetch Field Groups when selectedFormGroupId changes
  useEffect(() => {
    if (!selectedFormGroupId) return;
    fetchData();
  }, [selectedFormGroupId, projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getFieldGroups(projectId, selectedFormGroupId);
      setGroupsData({
        groups: res.groups || [],
        ungroupedFields: res.ungroupedFields || []
      });
    } catch (err) {
      toast.error('Failed to load field groups');
    } finally {
      setLoading(false);
    }
  };

  // Combine all fields available in the current FormGroup
  const allFormGroupFields = useMemo(() => {
    const grouped = (groupsData.groups || []).flatMap(g => g.fields || []);
    const ungrouped = groupsData.ungroupedFields || [];
    return [...grouped, ...ungrouped];
  }, [groupsData]);

  // Open Create Modal
  const handleOpenCreateModal = (preselectedFieldIds = []) => {
    setGroupLabelInput('');
    setSelectedFieldIds(preselectedFieldIds);
    setShowCreateModal(true);
  };

  // Create Field Group Submit
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!groupLabelInput.trim()) {
      toast.error('Please enter a group label');
      return;
    }
    if (selectedFieldIds.length === 0) {
      toast.error('Please select at least one field');
      return;
    }

    try {
      setSubmitting(true);
      const res = await createFieldGroup(projectId, selectedFormGroupId, {
        groupLabel: groupLabelInput.trim(),
        fieldIds: selectedFieldIds
      });
      toast.success(res.message || 'Field group created successfully');
      setGroupLabelInput('');
      setSelectedFieldIds([]);
      setFieldSearch('');
      setShowCreateModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create field group');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Edit Modal
  const handleOpenEditModal = (group) => {
    setActiveGroupLabel(group.groupLabel);
    setGroupLabelInput(group.groupLabel);
    const memberIds = (group.fields || []).map(f => f.id);
    setSelectedFieldIds(memberIds);
    setShowEditModal(true);
  };

  // Update Field Group Submit
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!groupLabelInput.trim()) {
      toast.error('Please enter a group label');
      return;
    }

    try {
      setSubmitting(true);
      const res = await updateFieldGroup(projectId, selectedFormGroupId, activeGroupLabel, {
        newGroupLabel: groupLabelInput.trim(),
        fieldIds: selectedFieldIds
      });
      toast.success(res.message || 'Field group updated successfully');
      setGroupLabelInput('');
      setSelectedFieldIds([]);
      setFieldSearch('');
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update field group');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Delete Modal
  const handleOpenDeleteModal = (groupLabel) => {
    setActiveGroupLabel(groupLabel);
    setShowDeleteModal(true);
  };

  // Delete Field Group Confirm
  const handleDeleteConfirm = async () => {
    try {
      setSubmitting(true);
      const res = await deleteFieldGroup(projectId, selectedFormGroupId, activeGroupLabel);
      toast.success(res.message || 'Field group deleted successfully');
      setShowDeleteModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete field group');
    } finally {
      setSubmitting(false);
    }
  };

  // Reorder Group (Move Up / Move Down)
  const handleMoveGroup = async (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= groupsData.groups.length) return;

    const newGroups = [...groupsData.groups];
    const [movedGroup] = newGroups.splice(index, 1);
    newGroups.splice(targetIndex, 0, movedGroup);

    setGroupsData(prev => ({ ...prev, groups: newGroups }));

    try {
      const orderedGroupLabels = newGroups.map(g => g.groupLabel);
      await reorderFieldGroups(projectId, selectedFormGroupId, orderedGroupLabels);
      toast.success('Group order updated');
    } catch (err) {
      toast.error('Failed to update group order');
      fetchData();
    }
  };

  // Field selection toggle helper
  const toggleFieldSelection = (fieldId) => {
    setSelectedFieldIds(prev =>
      prev.includes(fieldId) ? prev.filter(id => id !== fieldId) : [...prev, fieldId]
    );
  };

  // Toggle all available fields in modal
  const toggleSelectAllFields = (fieldList) => {
    const listIds = fieldList.map(f => f.id);
    const allSelected = listIds.every(id => selectedFieldIds.includes(id));
    if (allSelected) {
      setSelectedFieldIds(prev => prev.filter(id => !listIds.includes(id)));
    } else {
      setSelectedFieldIds(prev => Array.from(new Set([...prev, ...listIds])));
    }
  };

  // Filter fields in modals
  const filteredModalFields = useMemo(() => {
    if (!fieldSearch.trim()) return allFormGroupFields;
    const term = fieldSearch.toLowerCase();
    return allFormGroupFields.filter(f =>
      (f.label && f.label.toLowerCase().includes(term)) ||
      (f.name && f.name.toLowerCase().includes(term)) ||
      (f.type && f.type.toLowerCase().includes(term))
    );
  }, [allFormGroupFields, fieldSearch]);

  const totalGroupedFieldsCount = useMemo(() => {
    return (groupsData.groups || []).reduce((sum, g) => sum + (g.fields?.length || 0), 0);
  }, [groupsData]);

  return (
    <Container title="Form Field Groups Manager">
      {/* Header & FormGroup Selector */}
      <Card className="mb-4 shadow-sm border-0 bg-white">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center gap-3">
                <div className="p-3 bg-primary bg-opacity-10 text-primary rounded-3">
                  <Tags size={28} />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold">Field Groups by Label</h5>
                  <p className="text-muted mb-0 small">
                    Organize form fields into logical labeled sections for easier management.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6} className="mt-3 mt-md-0">
              <Form.Group className="d-flex align-items-center justify-content-md-end gap-2">
                <Form.Label className="mb-0 fw-semibold text-nowrap">Form Group:</Form.Label>
                <Form.Select
                  style={{ maxWidth: '300px' }}
                  value={selectedFormGroupId}
                  onChange={(e) => setSelectedFormGroupId(e.target.value)}
                  disabled={loading}
                >
                  {formGroups.map(fg => (
                    <option key={fg.id} value={fg.id}>
                      {fg.name} {fg.isActive ? '(Active)' : ''}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="primary"
                  className="d-flex align-items-center gap-2 text-nowrap"
                  onClick={() => handleOpenCreateModal()}
                  disabled={loading || !selectedFormGroupId}
                >
                  <FolderPlus size={18} /> New Field Group
                </Button>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Summary Statistics Bar */}
      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm bg-gradient text-dark" style={{ backgroundColor: '#f8f9fa' }}>
            <Card.Body className="d-flex align-items-center justify-content-between p-3">
              <div>
                <span className="text-muted small text-uppercase fw-bold">Total Label Groups</span>
                <h3 className="mb-0 fw-bold text-primary">{groupsData.groups?.length || 0}</h3>
              </div>
              <Layers size={32} className="text-primary opacity-50" />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
            <Card.Body className="d-flex align-items-center justify-content-between p-3">
              <div>
                <span className="text-muted small text-uppercase fw-bold">Grouped Fields</span>
                <h3 className="mb-0 fw-bold text-success">{totalGroupedFieldsCount}</h3>
              </div>
              <Check2Circle size={32} className="text-success opacity-50" />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading field groups...</p>
        </div>
      ) : (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-white py-3">
            <h6 className="fw-bold mb-0 text-uppercase text-secondary">Labeled Groups Order & Actions</h6>
          </Card.Header>
          <Card.Body className="p-0">
            {groupsData.groups?.length === 0 ? (
              <Alert variant="light" className="border text-center py-4 m-3">
                <p className="mb-1 text-muted">No labeled field groups created yet for this Form Group.</p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={() => handleOpenCreateModal()}
                >
                  Create First Field Group
                </Button>
              </Alert>
            ) : (
              <Table hover responsive className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '80px' }}>Order</th>
                    <th style={{ width: '220px' }}>Group Label</th>
                    <th>Fields</th>
                    <th style={{ width: '180px' }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupsData.groups.map((group, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold text-muted">{idx + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold text-dark">{group.groupLabel}</span>
                          <Badge bg="secondary" pill>
                            {group.fields?.length || 0} fields
                          </Badge>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {group.fields?.map(f => (
                            <Badge bg="light" text="dark" className="border fw-normal" key={f.id}>
                              {f.label || f.name} <span className="text-muted">({f.name})</span>
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-1">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            title="Move Up"
                            disabled={idx === 0}
                            onClick={() => handleMoveGroup(idx, 'up')}
                          >
                            <ArrowUpShort size={16} />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            title="Move Down"
                            disabled={idx === groupsData.groups.length - 1}
                            onClick={() => handleMoveGroup(idx, 'down')}
                          >
                            <ArrowDownShort size={16} />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            title="Edit Group"
                            onClick={() => handleOpenEditModal(group)}
                          >
                            <PencilSquare size={14} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Delete Group"
                            onClick={() => handleOpenDeleteModal(group.groupLabel)}
                          >
                            <Trash3 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* CREATE GROUP MODAL */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Form onSubmit={handleCreateSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="h5 fw-bold">Create New Field Group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Group Label <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Personal Details, Store Audit Info..."
                value={groupLabelInput}
                onChange={(e) => setGroupLabelInput(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                This label will be assigned to all selected fields for this FormGroup.
              </Form.Text>
            </Form.Group>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label className="fw-semibold mb-0">Select Member Fields ({selectedFieldIds.length} selected)</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  size="sm"
                  placeholder="Filter fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  style={{ width: '180px' }}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => toggleSelectAllFields(filteredModalFields)}
                >
                  Toggle All
                </Button>
              </div>
            </div>

            <div className="border rounded p-2" style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {filteredModalFields.length === 0 ? (
                <div className="text-center text-muted p-3">No fields match your search.</div>
              ) : (
                filteredModalFields.map(f => {
                  const isChecked = selectedFieldIds.includes(f.id);
                  const currentGroup = (groupsData.groups || []).find(g => (g.fields || []).some(m => m.id === f.id));
                  return (
                    <div
                      key={f.id}
                      className={`d-flex align-items-center justify-content-between p-2 rounded mb-1 border-bottom ${isChecked ? 'bg-primary bg-opacity-10' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleFieldSelection(f.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`create-field-${f.id}`}
                        checked={isChecked}
                        onChange={() => {}} // handled by parent onClick
                        label={
                          <span>
                            <strong className="me-2">{f.label || f.name}</strong>
                            <code className="text-muted me-2">({f.name})</code>
                            <Badge bg="secondary" size="sm">{f.type}</Badge>
                          </span>
                        }
                      />
                      {currentGroup && (
                        <Badge bg="info" text="dark" className="ms-2">
                          Current: {currentGroup.groupLabel}
                        </Badge>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : 'Create Group'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* EDIT GROUP MODAL */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Form onSubmit={handleUpdateSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="h5 fw-bold">Edit Field Group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Group Label <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={groupLabelInput}
                onChange={(e) => setGroupLabelInput(e.target.value)}
                required
              />
            </Form.Group>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label className="fw-semibold mb-0">Select Member Fields ({selectedFieldIds.length} selected)</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  size="sm"
                  placeholder="Filter fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  style={{ width: '180px' }}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => toggleSelectAllFields(filteredModalFields)}
                >
                  Toggle All
                </Button>
              </div>
            </div>

            <div className="border rounded p-2" style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {filteredModalFields.map(f => {
                const isChecked = selectedFieldIds.includes(f.id);
                return (
                  <div
                    key={f.id}
                    className={`d-flex align-items-center justify-content-between p-2 rounded mb-1 border-bottom ${isChecked ? 'bg-primary bg-opacity-10' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleFieldSelection(f.id)}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`edit-field-${f.id}`}
                      checked={isChecked}
                      onChange={() => {}}
                      label={
                        <span>
                          <strong className="me-2">{f.label || f.name}</strong>
                          <code className="text-muted me-2">({f.name})</code>
                          <Badge bg="secondary" size="sm">{f.type}</Badge>
                        </span>
                      }
                    />
                  </div>
                );
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold text-danger">Delete Field Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the field group <strong>"{activeGroupLabel}"</strong>?
          <p className="text-muted small mt-2 mb-0">
            This will un-group all fields under this label. The fields themselves will not be deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Delete Group'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
